import AutoModeIcon from '@mui/icons-material/AutoMode'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import clsx from 'clsx'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Switch,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import UserChangePasswordFormScope from '../ucp_form_scope'

const LOG = Logger.get('UserChangePasswordFormViewClass')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        minHeight: 400,
        minWidth: 500,
        rowGap: 10
    },
    dialogTitle: {
        backgroundColor: '#030303',
        color: '#6cea48'
    },
    flexGround: {
        flexGrow: 1
    }
})

type UserChangePasswordFormViewProps = {
    className?: string
    scope: UserChangePasswordFormScope
}
class UserChangePasswordFormViewClass implements FCClassContext<UserChangePasswordFormViewProps> {
    // :: Fields
    scope!: UserChangePasswordFormScope
    sytles!: ReturnType<typeof useStyles>

    // :: emissors

    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly emitOnCancel = () => {
        this.scope.onCancel().catch(LOG.caught)
    }

    readonly emitOnGenerate = () => {
        this.scope.onGenerate().catch(LOG.caught)
    }

    readonly handleTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: string) => {
        this.scope.type = newType as 'password' | 'generate'
    }

    readonly handleClickShowPassword = () => {
        this.scope.showPassword = !this.scope.showPassword
    }

    readonly handleClickShowConfirmPassword = () => {
        this.scope.showConfirmPassword = !this.scope.showConfirmPassword
    }

    // :: Methods

    onSyncState({ scope }: UserChangePasswordFormViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: UserChangePasswordFormViewProps) {
        this.scope = bindUpdate(React, scope)

        const classes = this.sytles.classes

        return (
            <div className={clsx(classes.view, className)}>
                <Card
                    elevation={3}
                    style={{ padding: 0 }}
                    sx={{
                        overflowY: 'auto'
                    }}
                >
                    <CardHeader className={classes.dialogTitle} title="Alterar Senha do Usuário" />
                    <CardContent>
                        <ToggleButtonGroup
                            color="primary"
                            value={this.scope.type}
                            exclusive
                            onChange={this.handleTypeChange}
                            aria-label="Platform"
                        >
                            <ToggleButton value="password">Informar Senha</ToggleButton>
                            <ToggleButton value="generate">Gerar Senha</ToggleButton>
                        </ToggleButtonGroup>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'baseline',
                                gap: '10px',
                                marginBottom: '20px',
                                marginTop: '20px'
                            }}
                        >
                            {scope.type === 'password' ? (
                                <TextField
                                    className={classes.flexGround}
                                    defaultValue={scope.newPassword || ''}
                                    type={this.scope.showPassword ? 'text' : 'password'}
                                    onChange={(evt) => (scope.newPassword = evt.target.value)}
                                    label="Nova Senha"
                                    variant="standard"
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={this.handleClickShowPassword}
                                                        edge="end"
                                                    >
                                                        {scope.showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }
                                    }}
                                />
                            ) : (
                                <TextField
                                    className={classes.flexGround}
                                    type={this.scope.showPassword ? 'text' : 'password'}
                                    value={scope.newPassword}
                                    label="Nova Senha"
                                    variant="standard"
                                    slotProps={{
                                        input: {
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="generate password"
                                                        onClick={this.emitOnGenerate}
                                                        edge="end"
                                                    >
                                                        <AutoModeIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={this.handleClickShowPassword}
                                                        edge="end"
                                                    >
                                                        {scope.showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }
                                    }}
                                />
                            )}

                            {scope.type === 'password' && (
                                <TextField
                                    className={classes.flexGround}
                                    defaultValue={scope.confirmNewPassword || ''}
                                    type={scope.showConfirmPassword ? 'text' : 'password'}
                                    onChange={(evt) => (scope.confirmNewPassword = evt.target.value)}
                                    label="Confirmação de Senha"
                                    variant="standard"
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={this.handleClickShowConfirmPassword}
                                                        edge="end"
                                                    >
                                                        {scope.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }
                                    }}
                                />
                            )}
                        </Box>

                        <FormControlLabel
                            value="sendToEmail"
                            control={
                                <Switch
                                    defaultChecked={scope.sendToEmail}
                                    onChange={(evt) => (scope.sendToEmail = evt.target.checked)}
                                    name="sendToEmail"
                                />
                            }
                            label="Enviar senha por Email"
                            labelPlacement="end"
                        />
                    </CardContent>

                    <CardActions disableSpacing>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'baseline',
                                gap: '10px',
                                marginTop: '10px'
                            }}
                        >
                            <Button variant="outlined" onClick={this.emitOnSave}>
                                Salvar
                            </Button>
                            <Button variant="outlined" onClick={this.emitOnCancel}>
                                Cancelar
                            </Button>
                        </Box>
                    </CardActions>
                </Card>
            </div>
        )
    }
}

export const UserChangePasswordFormView = classToFComponent(UserChangePasswordFormViewClass, React)
export default UserChangePasswordFormView
